import { ThemeProvider, Link, CssBaseline, Container, IconButton, useMediaQuery, Box } from "@mui/material";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { SnackbarProvider, MaterialDesignContent, closeSnackbar } from "notistack";
import { styled, keyframes } from "@mui/material/styles";
import { get as _get } from "lodash";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useBlockHeight } from "../../hooks/queries";
import { useExplorer } from "../../hooks/useExplorer";
import { explorerUrl } from "../../utils/explorer";

// icons

// components
import Header from "../Header";
import Modals from "../Modals";

// themes
import themeOptions from "../../theme/main";
import { BottomNav } from "../BottomNav";

// built once, Layout renders again on every block height refresh
const appTheme = responsiveFontSizes(createTheme(themeOptions));

const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

// notifications on a raised surface, the border carries the status color
const Notification = styled(MaterialDesignContent)(({ theme }) => ({
  "&.notistack-MuiContent": {
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.9375rem",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: "0 16px 40px rgba(0, 0, 0, 0.5)",
  },
  "&.notistack-MuiContent-success": { borderColor: theme.palette.primary.main },
  "&.notistack-MuiContent-error": { borderColor: theme.palette.error.main },
  "&.notistack-MuiContent-warning": { borderColor: theme.palette.warning.main },
  "&.notistack-MuiContent-success svg": { color: theme.palette.primary.main },
  "&.notistack-MuiContent-error svg": { color: theme.palette.error.main },
  "&.notistack-MuiContent-warning svg": { color: theme.palette.warning.main },
}));
const notificationComponents = {
  default: Notification,
  success: Notification,
  error: Notification,
  warning: Notification,
  info: Notification,
};

const SnackbarActions = (key) => (
  <IconButton component="span" aria-label="close" onClick={() => closeSnackbar(key)}>
    <CloseIcon sx={{ color: "text.main" }} />
  </IconButton>
);

const Layout = () => {
  const matches = useMediaQuery(appTheme.breakpoints.down("sm"));

  const { data: blockheight = null } = useBlockHeight();
  const settingSelector = useSelector((state) => state.settings);
  const useapi = _get(settingSelector, "useapi", true);
  const explorer = useExplorer();

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <SnackbarProvider
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={4000}
        Components={notificationComponents}
        action={(key) => SnackbarActions(key)}
      >
        <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
          <Header />
          <Container maxWidth="lg">
            <Outlet />
            <Modals />
          </Container>
          {blockheight != null && useapi === true ? (
            <Link
              href={explorerUrl(explorer, "block", blockheight)}
              target="_blank"
              rel="noopener noreferrer"
              title={`Latest block, open it in ${explorer.name}`}
              underline="none"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                gap: 1,
                position: "fixed",
                bottom: 16,
                right: 16,
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                border: 1,
                borderColor: "divider",
                backgroundColor: "background.paper",
                color: "text.secondary",
                fontSize: "0.8125rem",
                "&:hover": { color: "text.primary" },
              }}
            >
              {/* the only ambient motion: the chain is live */}
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  animation: `${livePulse} 2.4s ease-in-out infinite`,
                }}
              />
              Block {Number(blockheight).toLocaleString("en-US")}
            </Link>
          ) : (
            ""
          )}
          {matches ? <BottomNav sx={{ width: "100%", position: "fixed", bottom: "0px", overflow: "hidden" }} /> : null}
        </Box>
      </SnackbarProvider>
    </ThemeProvider>
  );
};
export default Layout;
